"use client";

import { useState } from "react";
import Link from "next/link";
import { CheckCircle2, ChevronDown, Loader2, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { trackEvent } from "@/lib/analytics";
import posthog from "posthog-js";
import { getDistinctIdHeader, identifyLead, useFormTracking } from "@/lib/posthog-tracking";
import { TurnstileWidget } from "@/components/shared/turnstile-widget";

interface CallbackFormProps {
  source: string;
  onSuccess?: () => void;
  defaultShowOptional?: boolean;
}

const PHONE_DIGITS_MIN = 9;

function stripPhone(value: string) {
  return value.replace(/[^\d+]/g, "");
}

function isPhoneValid(value: string) {
  const cleaned = stripPhone(value);
  if (cleaned.startsWith("+")) return cleaned.length >= 10 && cleaned.length <= 16;
  return cleaned.length === PHONE_DIGITS_MIN;
}

export function CallbackForm({ source, onSuccess, defaultShowOptional = false }: CallbackFormProps) {
  const [phone, setPhone] = useState("");
  const [callbackTime, setCallbackTime] = useState("");
  const [topic, setTopic] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [showOptional, setShowOptional] = useState(defaultShowOptional);
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  const phoneValid = isPhoneValid(phone);
  const formTracking = useFormTracking("callback_form", { source });

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!phoneValid) {
      setError("Podaj poprawny numer telefonu (np. 600 700 800)");
      return;
    }

    setIsLoading(true);
    setError(null);

    trackEvent({
      event: "callback_form_submitted",
      category: "conversion",
      label: source,
    });
    posthog.capture("callback_form_submitted", { source });

    try {
      const response = await fetch("/api/demo", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getDistinctIdHeader(),
        },
        body: JSON.stringify({
          phone,
          callback_time: callbackTime,
          topic,
          source,
          website,
          turnstile_token: turnstileToken,
        }),
      });

      if (!response.ok) {
        throw new Error("Wystąpił błąd. Spróbuj ponownie.");
      }

      trackEvent({
        event: "callback_request",
        category: "conversion",
        label: source,
      });
      posthog.capture("callback_request", { source });
      posthog.capture("survey_trigger_post_lead", { source, lead_type: "callback" });
      formTracking.onSubmitted();
      identifyLead({
        phone,
        source,
        extra: {
          has_callback_time: !!callbackTime,
          has_topic: !!topic,
        },
      });

      if (typeof window !== "undefined" && typeof window.fbq === "function") {
        window.fbq("track", "Lead");
      }

      setSubmitted(true);
      onSuccess?.();
    } catch (err) {
      trackEvent({
        event: "callback_form_error",
        category: "conversion",
        label: err instanceof Error ? err.message : "unknown_error",
      });
      posthog.capture("callback_form_error", { source, error: err instanceof Error ? err.message : "unknown_error" });
      posthog.captureException(err instanceof Error ? err : new Error(String(err)));
      setError(err instanceof Error ? err.message : "Wystąpił błąd");
    } finally {
      setIsLoading(false);
    }
  }

  if (submitted) {
    return (
      <div className="text-center py-6">
        <div className="mx-auto h-14 w-14 rounded-full bg-emerald-50 flex items-center justify-center">
          <CheckCircle2 className="h-7 w-7 text-emerald-600" />
        </div>
        <h3 className="mt-4 text-xl font-semibold">Mamy Twój numer.</h3>
        <p className="mt-2 text-sm text-muted-foreground max-w-sm mx-auto">
          Oddzwonimy w ciągu jednego dnia roboczego, w godzinach 8:00–22:00.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor={`${source}-phone`}>Numer telefonu</Label>
        <div className="relative">
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            id={`${source}-phone`}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="600 700 800"
            value={phone}
            onChange={(e) => {
              setPhone(e.target.value);
              if (error) setError(null);
            }}
            onFocus={formTracking.onFirstInteraction}
            required
            disabled={isLoading}
            className="h-12 rounded-xl border-black/[0.08] bg-[#F5F7FB] pl-10 text-base transition-colors focus-visible:bg-white"
          />
        </div>
      </div>

      <Button
        type="submit"
        size="lg"
        className="w-full h-12 rounded-xl text-base font-semibold shadow-lg shadow-primary/25"
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Wysyłanie...
          </>
        ) : (
          "Oddzwonimy"
        )}
      </Button>

      {error && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
          {error}
        </p>
      )}

      <p className="text-sm text-muted-foreground">
        Oddzwaniamy w godzinach 8:00–22:00.
      </p>

      <button
        type="button"
        onClick={() => setShowOptional((v) => !v)}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <span>Dodaj szczegóły (opcjonalne)</span>
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform ${showOptional ? "rotate-180" : ""}`}
        />
      </button>

      {showOptional && (
        <div className="space-y-3 pt-1">
          <div className="space-y-1.5">
            <Label htmlFor={`${source}-callback-time`} className="text-sm font-normal text-muted-foreground">
              Kiedy najlepiej zadzwonić?
            </Label>
            <Input
              id={`${source}-callback-time`}
              type="text"
              placeholder="np. dziś po 17:00 / jutro rano"
              value={callbackTime}
              onChange={(e) => setCallbackTime(e.target.value)}
              disabled={isLoading}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${source}-topic`} className="text-sm font-normal text-muted-foreground">
              O czym chcesz porozmawiać?
            </Label>
            <textarea
              id={`${source}-topic`}
              placeholder="np. migracja z Klinika XP, ceny dla 4 lekarzy"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              disabled={isLoading}
              rows={3}
              className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 resize-none"
            />
          </div>
        </div>
      )}

      <TurnstileWidget onToken={setTurnstileToken} />

      {/* Honeypot */}
      <div className="absolute -left-[9999px] opacity-0 h-0 w-0 overflow-hidden" aria-hidden="true">
        <Label htmlFor={`${source}-website`}>Website</Label>
        <Input
          id={`${source}-website`}
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
        />
      </div>

      <p className="text-xs text-muted-foreground/80 leading-relaxed">
        Klikając „Oddzwonimy”, wyrażam zgodę na kontakt telefoniczny przez
        Vetina Prosta Spółka Akcyjna (KRS 0001226831, NIP 7773464768) zgodnie
        z{" "}
        <Link
          href="/regulamin"
          className="text-primary hover:underline"
          target="_blank"
        >
          Regulaminem
        </Link>{" "}
        i{" "}
        <Link
          href="/polityka-prywatnosci"
          className="text-primary hover:underline"
          target="_blank"
        >
          Polityką prywatności
        </Link>
        .
      </p>
    </form>
  );
}
